import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ApiService from '../../api/ApiService';
import { useAuth } from '../auth/AuthContext';
import {
  formatTaskPoints,
  formatTaskTime,
  getTaskModelName,
  getTaskPreview,
  getTaskStatusMeta
} from '../../utils/taskRecord';
import './WorkspaceLayout.css';

const RECENT_TASK_LIMIT = 3;

function WorkspaceLayout({ children }) {
  const { user, isAdmin, openLogin, updateUser, logout } = useAuth();
  const accountRef = useRef(null);
  const copyResetTimer = useRef(null);
  const nameFeedbackResetTimer = useRef(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [pointsBalance, setPointsBalance] = useState(null);
  const [isBalanceLoading, setIsBalanceLoading] = useState(Boolean(user?.token));
  const [balanceUnavailable, setBalanceUnavailable] = useState(false);
  const [recentTasks, setRecentTasks] = useState([]);
  const [recentTaskTotal, setRecentTaskTotal] = useState(0);
  const [areTasksLoading, setAreTasksLoading] = useState(Boolean(user?.token));
  const [tasksUnavailable, setTasksUnavailable] = useState(false);
  const [idCopied, setIdCopied] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const [nameError, setNameError] = useState('');
  const [nameFeedback, setNameFeedback] = useState('');

  const displayName = user?.name || user?.phone || `用户 ${user?.id || ''}`;
  const avatarInitial = String(displayName || '用').trim().charAt(0).toUpperCase() || '用';

  useEffect(() => {
    if (!isEditingName) setNameDraft(displayName);
  }, [displayName, isEditingName]);

  useEffect(() => {
    if (isProfileOpen) return;
    setIsEditingName(false);
    setIsSavingName(false);
    setNameError('');
    setNameFeedback('');
  }, [isProfileOpen]);

  useEffect(() => {
    let active = true;

    const loadAccountData = async () => {
      if (!user?.token) {
        setIsBalanceLoading(false);
        setAreTasksLoading(false);
        return;
      }

      const api = new ApiService();
      const [balanceResult, tasksResult] = await Promise.allSettled([
        api.get_user_money(user.token, { silent: true }),
        api.get_fal_task_list(user.token, 1, RECENT_TASK_LIMIT, '', '', { silent: true })
      ]);
      if (!active) return;

      if (balanceResult.status === 'fulfilled') {
        const nextBalance = Number(balanceResult.value?.points_balance);
        setPointsBalance(Number.isFinite(nextBalance) ? nextBalance : null);
        setBalanceUnavailable(!Number.isFinite(nextBalance));
      } else {
        setBalanceUnavailable(true);
      }

      if (tasksResult.status === 'fulfilled' && tasksResult.value?.code === 200) {
        const taskData = tasksResult.value?.data || {};
        const taskList = Array.isArray(taskData.list) ? taskData.list : [];
        setRecentTasks(taskList.slice(0, RECENT_TASK_LIMIT));
        setRecentTaskTotal(Number(taskData.total) || taskList.length);
      } else {
        setTasksUnavailable(true);
      }

      setIsBalanceLoading(false);
      setAreTasksLoading(false);
    };

    loadAccountData();
    return () => {
      active = false;
    };
  }, [user?.token]);

  useEffect(() => {
    const handleBalanceUpdated = (event) => {
      const nextBalance = Number(event?.detail?.pointsBalance);
      if (!Number.isFinite(nextBalance)) return;
      setPointsBalance(nextBalance);
      setBalanceUnavailable(false);
      setIsBalanceLoading(false);
    };

    window.addEventListener('canvas:balance-updated', handleBalanceUpdated);
    return () => window.removeEventListener('canvas:balance-updated', handleBalanceUpdated);
  }, []);

  useEffect(() => {
    if (!isProfileOpen) return undefined;

    const closeOnOutsideClick = (event) => {
      if (accountRef.current && !accountRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setIsProfileOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isProfileOpen]);

  useEffect(() => () => {
    if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
    if (nameFeedbackResetTimer.current) clearTimeout(nameFeedbackResetTimer.current);
  }, []);

  const formatPoints = () => {
    if (isBalanceLoading) return '读取中…';
    if (balanceUnavailable || pointsBalance === null) return '暂不可用';
    return pointsBalance.toLocaleString('zh-CN', { maximumFractionDigits: 4 });
  };

  const handleCopyId = async () => {
    if (!user?.id || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(String(user.id));
      setIdCopied(true);
      if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
      copyResetTimer.current = setTimeout(() => setIdCopied(false), 1600);
    } catch (_) {
      // Clipboard access may be unavailable in insecure or restricted contexts.
    }
  };

  const handleLogout = () => {
    setIsProfileOpen(false);
    logout();
  };

  const startEditingName = () => {
    setNameDraft(displayName);
    setNameError('');
    setNameFeedback('');
    setIsEditingName(true);
  };

  const cancelEditingName = () => {
    if (isSavingName) return;
    setNameDraft(displayName);
    setNameError('');
    setIsEditingName(false);
  };

  const handleNameKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    cancelEditingName();
  };

  const handleNameSubmit = async (event) => {
    event.preventDefault();
    const nextName = nameDraft.trim();

    if (!nextName) {
      setNameError('用户名不能为空');
      return;
    }
    if (nextName.length > 100) {
      setNameError('用户名不能超过 100 个字符');
      return;
    }
    if (nextName === String(user?.name || '').trim()) {
      setIsEditingName(false);
      setNameError('');
      return;
    }

    setIsSavingName(true);
    setNameError('');
    try {
      const response = await new ApiService().update_user_info(user.token, nextName);
      if (response?.code !== 200) {
        throw new Error(response?.msg || '修改失败');
      }

      const savedName = String(response?.data?.name || nextName).trim() || nextName;
      updateUser({ name: savedName });
      setNameDraft(savedName);
      setIsEditingName(false);
      setNameFeedback('用户名已更新');
      if (nameFeedbackResetTimer.current) clearTimeout(nameFeedbackResetTimer.current);
      nameFeedbackResetTimer.current = setTimeout(() => setNameFeedback(''), 1800);
    } catch (_) {
      setNameError('修改失败，请稍后重试');
    } finally {
      setIsSavingName(false);
    }
  };

  return (
    <div className="canvas-workspace-shell">
      <header className="canvas-workspace-header">
        <div className="canvas-workspace-account">
          {!user?.token && (
            <button
              className="canvas-workspace-login"
              type="button"
              onClick={() => openLogin()}
            >
              登录
            </button>
          )}
        </div>
      </header>

      {user?.token && (
        <aside className="canvas-workspace-profile-rail" ref={accountRef} aria-label="个人中心">
          <div className="canvas-workspace-profile">
            <button
              className={`canvas-workspace-profile-trigger${isProfileOpen ? ' is-open' : ''}`}
              type="button"
              aria-expanded={isProfileOpen}
              aria-controls="canvas-workspace-profile-card"
              aria-label={`${isProfileOpen ? '关闭' : '打开'}个人中心`}
              onClick={() => setIsProfileOpen((current) => !current)}
            >
              <span className="canvas-workspace-profile-avatar" aria-hidden="true">{avatarInitial}</span>
            </button>

            {isProfileOpen && (
              <section
                className="canvas-workspace-profile-card"
                id="canvas-workspace-profile-card"
                aria-label="个人中心"
              >
                <div className="canvas-workspace-profile-card-header">
                  <span className="canvas-workspace-profile-card-eyebrow">PERSONAL CENTER</span>
                  {isEditingName ? (
                    <form className="canvas-workspace-profile-name-form" onSubmit={handleNameSubmit}>
                      <label htmlFor="canvas-workspace-profile-name-input">用户名</label>
                      <div className="canvas-workspace-profile-name-controls">
                        <input
                          id="canvas-workspace-profile-name-input"
                          type="text"
                          value={nameDraft}
                          maxLength={100}
                          autoComplete="nickname"
                          autoFocus
                          disabled={isSavingName}
                          aria-invalid={Boolean(nameError)}
                          aria-describedby={nameError ? 'canvas-workspace-profile-name-error' : undefined}
                          onChange={(event) => {
                            setNameDraft(event.target.value);
                            if (nameError) setNameError('');
                          }}
                          onKeyDown={handleNameKeyDown}
                        />
                        <button type="submit" disabled={isSavingName} aria-label="保存用户名">
                          {isSavingName ? '保存中' : '保存'}
                        </button>
                        <button type="button" disabled={isSavingName} onClick={cancelEditingName} aria-label="取消修改用户名">
                          取消
                        </button>
                      </div>
                      {nameError && (
                        <span className="canvas-workspace-profile-name-message is-error" id="canvas-workspace-profile-name-error" role="alert">
                          {nameError}
                        </span>
                      )}
                    </form>
                  ) : (
                    <div className="canvas-workspace-profile-name-row">
                      <strong>{displayName}</strong>
                      <button type="button" onClick={startEditingName} aria-label="修改用户名">
                        <svg viewBox="0 0 16 16" aria-hidden="true">
                          <path d="M10.8 2.3a1.5 1.5 0 0 1 2.1 2.1L6.1 11.2l-2.8.6.6-2.8 6.9-6.7Z" />
                          <path d="m9.7 3.4 2.1 2.1" />
                        </svg>
                        修改
                      </button>
                      {nameFeedback && (
                        <span className="canvas-workspace-profile-name-message is-success" role="status">
                          {nameFeedback}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="canvas-workspace-profile-details">
                  <div className="canvas-workspace-profile-detail canvas-workspace-profile-points">
                    <div className="canvas-workspace-profile-points-heading">
                      <span>可用积分</span>
                      <Link to="/recharge" onClick={() => setIsProfileOpen(false)}>
                        充值积分 <span aria-hidden="true">→</span>
                      </Link>
                    </div>
                    <strong aria-live="polite">{formatPoints()}</strong>
                  </div>
                  <div className="canvas-workspace-profile-detail">
                    <span>用户 ID</span>
                    <strong>{user?.id || '—'}</strong>
                  </div>
                </div>

                <div className="canvas-workspace-profile-tasks">
                  <div className="canvas-workspace-profile-tasks-header">
                    <div>
                      <span>RECENT TASKS</span>
                      <strong>
                        最近任务
                        {recentTaskTotal > 0 && <small>{recentTaskTotal.toLocaleString('zh-CN')}</small>}
                      </strong>
                    </div>
                    <Link to="/task-records">
                      全部记录 <span aria-hidden="true">↗</span>
                    </Link>
                  </div>

                  <div className="canvas-workspace-profile-task-list" aria-live="polite">
                    {areTasksLoading && [0, 1, 2].map((item) => (
                      <div className="canvas-workspace-profile-task-skeleton" key={item} aria-hidden="true">
                        <i />
                        <span><b /><b /></span>
                      </div>
                    ))}

                    {!areTasksLoading && tasksUnavailable && (
                      <div className="canvas-workspace-profile-task-empty">任务记录暂不可用</div>
                    )}

                    {!areTasksLoading && !tasksUnavailable && recentTasks.length === 0 && (
                      <div className="canvas-workspace-profile-task-empty">暂无任务记录</div>
                    )}

                    {!areTasksLoading && !tasksUnavailable && recentTasks.map((task) => {
                      const preview = getTaskPreview(task);
                      const statusMeta = getTaskStatusMeta(task.status);
                      return (
                        <article className="canvas-workspace-profile-task" key={task.id || task.task_id}>
                          <div className="canvas-workspace-profile-task-media" aria-hidden="true">
                            <span>AI</span>
                            {preview?.type === 'image' && <img src={preview.url} alt="" loading="lazy" />}
                            {preview?.type === 'video' && (
                              <video src={preview.url} preload="metadata" muted playsInline />
                            )}
                          </div>
                          <div className="canvas-workspace-profile-task-body">
                            <div className="canvas-workspace-profile-task-topline">
                              <strong title={task.app_name}>{getTaskModelName(task.app_name)}</strong>
                              <span className={`canvas-workspace-profile-task-status ${statusMeta.className}`}>
                                <i /> {statusMeta.label}
                              </span>
                            </div>
                            <div className="canvas-workspace-profile-task-meta">
                              <time dateTime={task.created_at || ''}>{formatTaskTime(task.created_at)}</time>
                              <span>{formatTaskPoints(task.money)} 积分</span>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>

                <div className="canvas-workspace-profile-actions">
                  {isAdmin && (
                    <Link
                      className="canvas-workspace-profile-admin-link"
                      to="/admin"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      管理后台 <span aria-hidden="true">→</span>
                    </Link>
                  )}
                  <button type="button" onClick={handleCopyId} disabled={!user?.id}>
                    {idCopied ? '已复制 ID' : '复制用户 ID'}
                  </button>
                  <button className="canvas-workspace-profile-logout" type="button" onClick={handleLogout}>
                    退出登录
                  </button>
                </div>
              </section>
            )}
          </div>
        </aside>
      )}

      <div className="canvas-workspace-content">{children}</div>
    </div>
  );
}

export default WorkspaceLayout;
